import styled from "@emotion/styled";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import { ArchiveAPI } from "../../api/Archive";
import { ContactApi } from "../../api/Contacts";
import { getUser, UserAPI } from "../../api/User";
import { TestDriveBannerAtom } from "../../atoms/TestDriveBannerAtom";
import { InternalUserAtom, UserAtom } from "../../atoms/UserAtom";
import { UserEmailArchiveAtom } from "../../atoms/UserEmailArchiveAtom";
import { IStartPageOptions, IUserIRoute } from "../../generated/Api";
import useMediaQuery from "../../hook/useMediaQuery";
import { TEST_USERS } from "../../utils/constants";
import { localStorageSetItem } from "../../utils/LocalStorage";
import PageLoading from "../PageLoading";
import Footer from "./Footer";
import PasswordInput from "./PasswordInput";
import PhoneInput from "./PhoneInput";

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;

  background: linear-gradient(#fff, #fff2e5);

  display: flex;
  align-items: flex-start;
  justify-content: start;

  overflow-x: hidden;

  position: relative;

  padding-top: 128px;
  padding-left: 128px;

  @media (max-width: 1152px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Container = styled.div`
  max-width: 800px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 1152px) {
    justify-content: center;
    margin: auto;
  }
`;

const StyledImage = styled.img`
  max-height: 100vh;
  object-fit: cover;

  position: absolute;
  right: 0;
  bottom: 0;

  @media (max-width: 1153px) {
    opacity: 0.16;
  }
`;

const LogoContainer = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 9;

  @media (max-width: 576px) {
    position: unset;
    top: unset;
    left: unset;
    margin: 0 auto;
  }
`;

const Logo = styled.img`
  width: 180px;
`;

const LoginCard = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9;

  @media (max-width: 1152px) {
    height: auto;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.01);
    box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
    border-radius: 16px;

    padding: 32px;
    margin-left: unset;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  > div + div {
    margin-top: 12px;
  }

  div {
    color: #2f2f2f;
    font-size: 48px;
    font-weight: 900;
    font-family: "Goblin One", cursive;
    text-align: center;

    white-space: nowrap;

    @media (max-width: 1760px) {
      font-size: 44px;
    }

    @media (max-width: 1600px) {
      font-size: 40px;
    }

    @media (max-width: 1440px) {
      font-size: 36px;
    }

    @media (max-width: 1280px) {
      font-size: clamp(16px, 3vw, 28px);
    }

    @media (max-width: 1760px) and (max-height: 880px) {
      font-size: 36px;
    }

    @media (max-width: 1760px) and (max-height: 800px) {
      font-size: 32px;
    }

    @media (max-width: 1760px) and (max-height: 760px) {
      font-size: 28px;
    }

    @media (max-width: 1760px) and (max-height: 720px) {
      font-size: 24px;
    }

    @media (max-width: 1760px) and (max-height: 680px) {
      font-size: 20px;
    }

    @media (max-width: 560px) {
      font-size: 18px;
    }

    @media (max-width: 420px) {
      font-size: 16px;
    }
  }
`;

const SubTitleContainer = styled.div`
  color: #3e3e3e;
  font-size: clamp(14px, 2vw, 18px);
  text-align: center;

  margin: 32px 0;

  @media (max-height: 720px) {
    margin: 24px 0;
  }

  @media (max-height: 680px) {
    margin: 16px 0;
  }
`;

export interface ILogin {
  userUid: string;
  user: Partial<IUserIRoute> | null;
}

export default function Auth(): JSX.Element {
  const router = useRouter();
  const [isNumberInput, setIsNumberInput] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const setUser = useSetRecoilState(UserAtom);
  const setInternalUser = useSetRecoilState(InternalUserAtom);
  const setTestDriveBanner = useSetRecoilState(TestDriveBannerAtom);
  const [nextEnabled, setNextEnabled] = useState({ exists: false, type: null });
  const setUserEmailArchive = useSetRecoilState(UserEmailArchiveAtom);
  const isLargerThan600 = useMediaQuery("(min-width: 600px)");

  const login = async ({ userUid, user }: ILogin): Promise<void> => {
    if (!user.isActive) {
      toast("User is blocked", { type: "error" });
      setIsLoading(false);
    } else if (!user) {
      toast("No user found!", { type: "error" });
      setIsLoading(false);
      return;
    } else {
      const userToset = user as IUserIRoute & {
        billing: {
          admins?: number;
          externalUsers?: number;
          active?: boolean;
          testDriveExpiration?: number;
          testDriveActivated?: boolean;
        };
      };
      localStorageSetItem("USER_UID", user.userUid);

      localStorageSetItem("ADMIN_UID", user.adminUid || "");
      localStorageSetItem("ADMIN_AVATAR", user.avatar || "");

      localStorageSetItem("USER_EMAIL", "");
      localStorageSetItem("EXTRA_CARRIAGE", `${user.options?.extraCarriage}`);
      localStorageSetItem("FIRST_LINE_TITLE", `${user.options?.extraCarriage}`);
      setTestDriveBanner({
        show: !userToset.billing.active && userToset.billing.testDriveActivated,
        testDriveExpiration: userToset.billing.testDriveExpiration || 0,
      });
      setUser(userToset); //here

      let needsToCompleteCANSPAM =
        userToset.company.trim() === "" && userToset.address.trim() === "";

      let isOnTestDrive =
        !userToset.billing?.active && userToset.billing?.testDriveActivated;

      let needsToCreateFirstContact = false;

      if (isOnTestDrive) {
        let contactWithTestDriveTag = await ContactApi.getTestDriveContact();
        if (!contactWithTestDriveTag.exists) needsToCreateFirstContact = true;
      }

      if (needsToCompleteCANSPAM || needsToCreateFirstContact)
        router.replace("/complete-onboarding");
      else if (!userToset.options.doNotShowGettingStarted)
        router.replace("/getting-started");
      else if (user.options.startPage) {
        if (user.options.startPage === IStartPageOptions.ALL_EMAILS) {
          router.replace("/emails");
        } else if (user.options.startPage === IStartPageOptions.DASHBOARD) {
          router.replace("/dashboard");
        } else router.replace("/new-email");
      } else router.replace("/new-email");

      let archiveResponse = await ArchiveAPI.getDetail(user.userUid);
      let archive = archiveResponse;

      setUserEmailArchive(archive);
    }
  };

  useEffect(() => {
    localStorage.clear();
  }, []);

  async function onPasswordHandler(loginUser: {
    uid: string;
    authType: "LEARNISTIC" | "BERSERKER" | "INTERNAL";
  }): Promise<boolean> {
    setIsLoading(true);
    const { uid: userUid, authType } = loginUser;

    const isTestUser = TEST_USERS.includes(userUid);

    if (!isLargerThan600 && !isTestUser) {
      window.location.href = "/mobile-device";
      return;
    }

    if (authType === "INTERNAL") {
      // This is an internal user, so I must treat him differently and redirect
      // him to the "Select Account" screen

      localStorageSetItem("INTERNAL_USER_UID", userUid);

      const internalUser = await UserAPI.getInternalUser(userUid);

      setInternalUser(internalUser);

      router.replace("/account-selection");

      return false;
    }

    const user = await getUser(userUid);
    login({
      userUid,
      user: user as IUserIRoute,
    });

    return false;
  }

  return (
    <Wrapper>
      {isLoading && <PageLoading />}

      <LogoContainer>
        <Logo src="./icons/login/logo.svg" alt="Logo" />
      </LogoContainer>
      <Container>
        <LoginCard>
          <TitleContainer>
            <div>The World's Only</div> <div>Private Email Platform</div>
            <div>Created for Marketers</div> <div>by Marketers</div>
          </TitleContainer>
          <SubTitleContainer>
            {isNumberInput
              ? "Enter your mobile number to log into your account"
              : "Enter your 4-digit passcode"}
          </SubTitleContainer>
          {isNumberInput ? (
            <PhoneInput
              setIsNumberInput={() => setIsNumberInput(false)}
              nextEnabled={nextEnabled}
              handleNextEnabled={(vle: any) => setNextEnabled(vle)}
            />
          ) : (
            <PasswordInput
              setIsNumberInput={() => setIsNumberInput(true)}
              onComplete={onPasswordHandler}
              type={nextEnabled.type || ""}
            />
          )}
        </LoginCard>
        <Footer />
      </Container>
      <StyledImage src="./images/LoginImage.svg" alt="Illustration" />
    </Wrapper>
  );
}
